import { Injectable }            from '@angular/core';
import {
	ArchivingProceduralDocumentMalwareScanStatusEnum,
	ArchivingProceduralDocumentTypeEnum
}                                from '@app/modules/archiving/models/archiving-procedural-document.model';
import { OpenArchiveStatusEnum } from '@app/modules/archiving/models/open-archive.model';
import {
	MalwareScanStatusDTO,
	OpenArchiveStatusDTO,
	PDDocumentTypeDTO
}                                from '@openApi/generated/archiving-api';
import { LoggerService }         from '@shared/services/logger.service';

@Injectable( {
  providedIn: 'root'
} )
export class ArchivingMapperService {

  constructor(
		private readonly _logger: LoggerService
	) {	}

	get archiving() {
		return {
			// Open Archive
			openArchiveStatusDtoToTmo: this.mapOpenArchiveStatusDtoToTmo.bind( this ) as typeof this.mapOpenArchiveStatusDtoToTmo,
			openArchiveStatusTmoToDto: this.mapOpenArchiveStatusTmoToDto.bind( this ) as typeof this.mapOpenArchiveStatusTmoToDto,
			openArchiveStatusListTmoToDto: this.mapOpenArchiveStatusListTmoToDto.bind( this ) as typeof this.mapOpenArchiveStatusListTmoToDto,

			// Procedural Document
			procDocMalwareScanStatusDtoToTmo: this.mapProcDocMalwareScanStatusDtoToTmo.bind( this ) as typeof this.mapProcDocMalwareScanStatusDtoToTmo,
			proceduralDocumentTypeDtoToTmo: this.mapProceduralDocumentTypeDtoToTmo.bind( this ) as typeof this.mapProceduralDocumentTypeDtoToTmo,
			proceduralDocumentTypeTmoToDto: this.mapProceduralDocumentTypeTmoToDto.bind( this ) as typeof this.mapProceduralDocumentTypeTmoToDto
		};
	}

	// OPEN ARCHIVE ##############################################################
	private mapOpenArchiveStatusDtoToTmo( openArchiveStatusDto: OpenArchiveStatusDTO ): OpenArchiveStatusEnum {
		const openArchiveStatusDtoToTmoMap: { [ key in OpenArchiveStatusDTO ]: OpenArchiveStatusEnum } = {
			[ OpenArchiveStatusDTO.ARCHIVED ]: OpenArchiveStatusEnum.ARCHIVED,
			[ OpenArchiveStatusDTO.MALWARE_FOUND ]: OpenArchiveStatusEnum.MALWARE_FOUND,
			[ OpenArchiveStatusDTO.MALWARE_SCANNING ]: OpenArchiveStatusEnum.MALWARE_SCANNING
		};
		const openArchiveStatusTmo = openArchiveStatusDtoToTmoMap[ openArchiveStatusDto ] ?? null;
		if ( !openArchiveStatusTmo ) {
			this._logger.warn( `openArchiveStatusDtoToTmoMap has no matching TMO value ${ openArchiveStatusDto }` );
		}
		return openArchiveStatusTmo;
	}

	private mapOpenArchiveStatusTmoToDto( openArchiveStatus: OpenArchiveStatusEnum ): OpenArchiveStatusDTO {
		const openArchiveStatusTmoToDtoMap: { [ key in OpenArchiveStatusEnum ]: OpenArchiveStatusDTO } = {
			[ OpenArchiveStatusEnum.ARCHIVED ]: OpenArchiveStatusDTO.ARCHIVED,
			[ OpenArchiveStatusEnum.MALWARE_FOUND ]: OpenArchiveStatusDTO.MALWARE_FOUND,
			[ OpenArchiveStatusEnum.MALWARE_SCANNING ]: OpenArchiveStatusDTO.MALWARE_SCANNING
		};
		const openArchiveStatusDto = openArchiveStatusTmoToDtoMap[ openArchiveStatus ] ?? null;
		if ( !openArchiveStatusDto ) {
			this._logger.warn( `openArchiveStatusTmoToDtoMap has no matching DTO value ${ openArchiveStatus }` );
		}
		return openArchiveStatusDto;
	}

	private mapOpenArchiveStatusListTmoToDto( openArchiveStatusList: OpenArchiveStatusEnum[] ): OpenArchiveStatusDTO[] {
		return openArchiveStatusList?.map( this.mapOpenArchiveStatusTmoToDto );
	}


	// PROCEDURAL DOCUMENT ######################################################
	private mapProcDocMalwareScanStatusDtoToTmo( malwareScanStatusDto: MalwareScanStatusDTO ): ArchivingProceduralDocumentMalwareScanStatusEnum {
		const procDocMalwareScanStatusDtoToTmoMap: { [ key in MalwareScanStatusDTO ]: ArchivingProceduralDocumentMalwareScanStatusEnum } = {
			[ MalwareScanStatusDTO.FOUND ]: ArchivingProceduralDocumentMalwareScanStatusEnum.FOUND,
			[ MalwareScanStatusDTO.OK ]: ArchivingProceduralDocumentMalwareScanStatusEnum.OK,
			[ MalwareScanStatusDTO.SCANNING ]: ArchivingProceduralDocumentMalwareScanStatusEnum.SCANNING,
		};
		const malwareScanStatusTmo = procDocMalwareScanStatusDtoToTmoMap[ malwareScanStatusDto ] ?? null;
		if ( !malwareScanStatusTmo ) {
			this._logger.warn( `malwareScanStatusDtoToTmoMap has no matching TMO value ${ malwareScanStatusDto }` );
		}
		return malwareScanStatusTmo;
	}

	private mapProceduralDocumentTypeDtoToTmo( proceduralDocumentTypeDto: PDDocumentTypeDTO ): ArchivingProceduralDocumentTypeEnum {
		const proceduralDocumentTypeDtoToTmoMap: { [ key in PDDocumentTypeDTO ]: ArchivingProceduralDocumentTypeEnum } = {
			[ PDDocumentTypeDTO.PROCEDURAL_DOCUMENT ]: ArchivingProceduralDocumentTypeEnum.PROCEDURAL_DOCUMENT,
			[ PDDocumentTypeDTO.OTHER_APPLICABLE_DOCUMENT ]: ArchivingProceduralDocumentTypeEnum.OTHER_APPLICABLE_DOCUMENT
		};
		const proceduralDocumentTypeTmo = proceduralDocumentTypeDtoToTmoMap[ proceduralDocumentTypeDto ] ?? null;
		if ( !proceduralDocumentTypeTmo ) {
			this._logger.warn( `proceduralDocumentTypeDtoToTmoMap has no matching TMO value ${ proceduralDocumentTypeDto }` );
		}
		return proceduralDocumentTypeTmo;
	}

	private mapProceduralDocumentTypeTmoToDto( proceduralDocumentType: ArchivingProceduralDocumentTypeEnum ): PDDocumentTypeDTO {
		const proceduralDocumentTypeTmoToDtoMap: { [ key in ArchivingProceduralDocumentTypeEnum ]: PDDocumentTypeDTO } = {
			[ ArchivingProceduralDocumentTypeEnum.PROCEDURAL_DOCUMENT ]: PDDocumentTypeDTO.PROCEDURAL_DOCUMENT,
			[ ArchivingProceduralDocumentTypeEnum.OTHER_APPLICABLE_DOCUMENT ]: PDDocumentTypeDTO.OTHER_APPLICABLE_DOCUMENT
		};
		const proceduralDocumentTypeDto = proceduralDocumentTypeTmoToDtoMap[ proceduralDocumentType ] ?? null;
		if ( !proceduralDocumentTypeDto ) {
			this._logger.warn( `proceduralDocumentTypeTmoToDtoMap has no matching DTO value ${ proceduralDocumentType }` );
		}
		return proceduralDocumentTypeDto;
	}
}
